import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Stack,
  HStack,
  Text,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { useRouter } from "next/router";
import { generateQuestionsFromJd } from "./api/recruiter";
import {
  AI_QUESTION_GENERATOR_RETURN_KEY,
  getQuestionGeneratorReturn,
  saveQuestionGeneratorReturn,
} from "../lib/questionGeneratorSession";

const QUESTION_FORMATS = [
  { format: "MCQ", label: "Multiple Choice (MCQ)" },
  { format: "MRI", label: "Multiple Response (MRI)" },
  { format: "Fill in the Blanks", label: "Fill in the Blanks" },
  { format: "Descriptive", label: "Descriptive" },
  { format: "Coding", label: "Coding" },
];

const QUESTION_TYPE_OPTIONS = [
  {
    value: "Cognitive Ability",
    description: "Reasoning, Aptitude, Critical Thinking",
  },
  {
    value: "Technical Skills",
    description: "Coding, Problem Solving, Domain Knowledge",
  },
  {
    value: "Applied Skills",
    description: "Scenario-based, Simulation, Application",
  },
  {
    value: "Behavioral & Communication",
    description: "SJT, Personality, Communication",
  },
  {
    value: "Analytical Skills",
    description: "Data analysis, Interpretation",
  },
];

const DEFAULT_COUNTS = {
  MCQ: 2,
  MRI: 2,
  "Fill in the Blanks": 2,
  Descriptive: 2,
  Coding: 1,
};

const formatQuestionTypeForPrompt = (selectedValues = []) =>
  selectedValues
    .map((value) => {
      const option = QUESTION_TYPE_OPTIONS.find((opt) => opt.value === value);
      return option ? `${option.value} (${option.description})` : value;
    })
    .join("; ");

const buildJobDescription = ({
  role,
  skills,
  sector,
  questionTypes,
  criteria,
}) => {
  const parts = [];

  if (role) {
    parts.push(`We are looking for a ${role}.`);
  }
  if (skills) {
    parts.push(
      `The candidate should have experience in ${skills}.`
    );
  }
  if (sector) {
    parts.push(`Industry/sector: ${sector}.`);
  }
  if (questionTypes?.length) {
    parts.push(
      `Focus question types on: ${formatQuestionTypeForPrompt(questionTypes)}.`
    );
  }
  if (criteria) {
    parts.push(`Additional criteria: ${criteria}.`);
  }

  return parts.join(" ").trim();
};

const CreateQuestionsModal = ({ isOpen, onClose, userId, companyId }) => {
  const [role, setRole] = useState("");
  const [difficulty, setDifficulty] = useState("Medium");
  const [sector, setSector] = useState("");
  const [skills, setSkills] = useState("");
  const [criteria, setCriteria] = useState("");
  const [questionTypes, setQuestionTypes] = useState([]);
  const [counts, setCounts] = useState(DEFAULT_COUNTS);
  const [loading, setLoading] = useState(false);

  const router = useRouter();
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) return;

    const saved = getQuestionGeneratorReturn();
    if (saved?.restore && saved.modal === "createQuestions" && saved.form) {
      const form = saved.form;
      setRole(form.role || "");
      setSkills(form.skills || "");
      setDifficulty(form.difficulty || "Medium");
      setSector(form.sector || "");
      setCriteria(form.criteria || "");
      setQuestionTypes(Array.isArray(form.questionTypes) ? form.questionTypes : []);
      setCounts({ ...DEFAULT_COUNTS, ...(form.counts || {}) });
      sessionStorage.setItem(
        AI_QUESTION_GENERATOR_RETURN_KEY,
        JSON.stringify({ ...saved, restore: false })
      );
      return;
    }

    setCounts({ ...DEFAULT_COUNTS });
    setDifficulty("Medium");
    setQuestionTypes([]);
  }, [isOpen]);

  const handleCountChange = (format, value) => {
    // Allow empty while typing so "0" is not stuck and becomes "01"
    if (value === "") {
      setCounts((prev) => ({ ...prev, [format]: "" }));
      return;
    }
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed)) return;
    setCounts((prev) => ({
      ...prev,
      [format]: Math.max(0, parsed),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!role.trim()) {
      toast({
        title: "Role is required.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (!skills.trim()) {
      toast({
        title: "Skills are required.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const question_types = QUESTION_FORMATS.map(({ format }) => ({
      question_format: format,
      count: Number(counts[format]) || 0,
    })).filter((qt) => qt.count > 0);

    if (question_types.length === 0) {
      toast({
        title: "Add at least one question.",
        description: "Set a count greater than 0 for at least one format.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const job_description = buildJobDescription({
      role: role.trim(),
      skills: skills.trim(),
      sector: sector.trim(),
      questionTypes,
      criteria: criteria.trim(),
    });

    // Use static company_id 100 if not provided (tools page / non-logged-in)
    const finalCompanyId = companyId || 100;
    const finalUserId = userId || 1;

    const payload = {
      company_id: Number(finalCompanyId),
      user_id: Number(finalUserId),
      difficulty,
      job_description,
      question_types,
    };

    setLoading(true);
    try {
      const data = await generateQuestionsFromJd(payload);

      let normalized;
      if (data && Array.isArray(data.data)) {
        normalized = data;
      } else if (Array.isArray(data)) {
        normalized = { data };
      } else if (data && Array.isArray(data.questions)) {
        normalized = { data: data.questions };
      } else {
        normalized = { data: [] };
      }

      setLoading(false);
      onClose();

      saveQuestionGeneratorReturn({
        path: router.asPath,
        modal: "createQuestions",
        form: {
          role: role.trim(),
          skills: skills.trim(),
          difficulty,
          sector: sector.trim(),
          criteria: criteria.trim(),
          questionTypes,
          counts,
        },
      });

      if (typeof window !== "undefined") {
        sessionStorage.setItem(
          "aiGeneratedQuestionsData",
          JSON.stringify(normalized)
        );
      }

      router.push({
        pathname: "/AiGeneratedQuestions",
        query: {
          companyId: finalCompanyId,
          skills: `"${skills.trim()}"`,
          ...(role.trim() ? { jobRole: role.trim() } : {}),
        },
      });
    } catch (error) {
      console.error("Error generating questions:", error);
      setLoading(false);
      toast({
        title: "Could not generate questions.",
        description:
          error.message || "An error occurred. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Questions</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form onSubmit={handleSubmit}>
            <FormControl id="role" mb={4} isRequired>
              <FormLabel>Role/Responsibility</FormLabel>
              <Input
                type="text"
                placeholder="Enter role/responsibility"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              />
            </FormControl>

            <FormControl id="skills" mb={4} isRequired>
              <FormLabel>Skills</FormLabel>
              <Input
                type="text"
                placeholder="Enter skills"
                value={skills}
                onChange={(e) => setSkills(e.target.value)}
              />
            </FormControl>

            <FormControl id="difficulty" mb={4} isRequired>
              <FormLabel>Difficulty</FormLabel>
              <Select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
              >
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </Select>
            </FormControl>

            <FormControl id="question-counts" mb={4}>
              <FormLabel>Number of Questions by Format</FormLabel>
              <Stack spacing={3}>
                {QUESTION_FORMATS.map(({ format, label }) => (
                  <HStack key={format} justify="space-between">
                    <Text flex="1">{label}</Text>
                    <Input
                      type="number"
                      min={0}
                      width="100px"
                      value={counts[format]}
                      placeholder="0"
                      onChange={(e) =>
                        handleCountChange(format, e.target.value)
                      }
                    />
                  </HStack>
                ))}
              </Stack>
            </FormControl>

            <FormControl id="question-types" mb={4}>
              <FormLabel>Question Type</FormLabel>
              <CheckboxGroup value={questionTypes} onChange={setQuestionTypes}>
                <Stack direction="column" spacing={3}>
                  {QUESTION_TYPE_OPTIONS.map(({ value, description }) => (
                    <Checkbox key={value} value={value} alignItems="flex-start">
                      <Text fontWeight="medium">{value}</Text>
                      <Text fontSize="xs" color="gray.500">
                        {description}
                      </Text>
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
            </FormControl>

            <FormControl id="sector" mb={4}>
              <FormLabel>Sector (Optional)</FormLabel>
              <Input
                type="text"
                placeholder="Enter sector (optional)"
                value={sector}
                onChange={(e) => setSector(e.target.value)}
              />
            </FormControl>

            <FormControl id="criteria" mb={4}>
              <FormLabel>Criteria (Optional)</FormLabel>
              <Textarea
                placeholder="Enter additional criteria (optional)"
                value={criteria}
                onChange={(e) => setCriteria(e.target.value)}
                rows={3}
              />
            </FormControl>

            <ModalFooter px={0}>
              <Button
                variant="ghost"
                mr={3}
                onClick={onClose}
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" colorScheme="blue" isDisabled={loading}>
                {loading ? <Spinner size="sm" /> : "Submit"}
              </Button>
            </ModalFooter>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateQuestionsModal;
